/**
 * Retenues sociales d'un employé, selon les assurances auxquelles il est soumis.
 *
 * Le serveur calcule la fiche de salaire officielle (`PayrollCalculator` et
 * `Assujettissements`) ; les écrans en affichent une estimation. Les deux
 * appliquaient des règles différentes : la fiche d'équipe, le relevé A4 et
 * l'espace de l'employé prélevaient AVS, AC, AANP et LPP à tout le monde, même
 * à un employé que sa fiche dit non soumis. Les règles vivent ici, une fois,
 * et suivent celles du serveur :
 *
 * - un interrupteur absent vaut « soumis » ;
 * - non soumis à l'AVS : aucune retenue AVS / AI / APG ;
 * - non soumis à l'AC : aucune retenue de chômage ;
 * - non soumis à la LAA, ou couvert pour les seuls accidents professionnels
 *   (« Prof. uniquement ») : aucune prime AANP ;
 * - non soumis à la LPP : aucune cotisation LPP ;
 * - le patron d'une raison individuelle (indépendant) : chômage et LPP
 *   seulement s'il les coche lui-même (une case jamais réglée vaut « non »).
 *
 * Les parts patronales suivent les mêmes interrupteurs ; la prime AAP de
 * l'employeur reste due tant que l'employé est soumis à la LAA, même couvert
 * pour les seuls accidents professionnels.
 *
 * Les taux AVS, AC et AANP sont ceux que l'entreprise a réglés (`soc.taux`),
 * que la fiche officielle applique aussi ; un taux absent ou hors bornes
 * (0 à 20 %) retombe sur celui de la fiche par défaut, de part et d'autre.
 */

import { TAUX_MAXIMUM_PAYS, tauxValide } from './charges';
import { auCentime } from './vat';
import { decimalesDuPays } from './currency';

/** Taux légaux de la fiche officielle, part employé, en %, à défaut de réglage (cf. `TauxCotisations`). */
export const TAUX_FICHE = Object.freeze({ avs: 5.3, ac: 1.1, aanp: 1.4, ijm: 0 });

/** Taux LPP retenu quand la fiche de l'employé n'en porte pas. */
export const TAUX_LPP_DEFAUT = 5;

/**
 * La couverture accidents se limite-t-elle aux accidents professionnels ?
 *
 * L'éditeur écrit « Prof. uniquement » ; toute autre valeur, et l'absence de
 * valeur, valent la couverture complète, comme côté serveur.
 *
 * @param {string} [couverture] couverture LAA de la fiche
 * @returns {boolean}
 */
export function professionnelleUniquement(couverture) {
  if (couverture == null) return false;
  const valeur = String(couverture).trim().toLowerCase();
  return valeur.startsWith('prof') && valeur.includes('uniquement');
}

/**
 * Assurances auxquelles l'employé est soumis.
 *
 * @param {object} [emp] employé, tel que l'écran le détient
 * @returns {{avs: boolean, ac: boolean, laa: boolean, aanp: boolean, lpp: boolean}}
 */
export function assujettissements(emp) {
  const e = emp || {};
  const laa = e.soumisLAA !== false;
  // Le patron d'une raison individuelle est indépendant : chômage et LPP ne
  // lui sont pas obligatoires — une case jamais réglée ne vaut donc pas
  // « soumis » pour lui ; il décide lui-même en cochant (comme
  // `RegimeDuPatron` côté serveur).
  const independant = e.patronIndependant === true;
  return {
    avs: e.soumisAVS !== false,
    ac: independant ? e.soumisAC === true : e.soumisAC !== false,
    laa,
    aanp: laa && !professionnelleUniquement(e.laa),
    lpp: independant ? e.soumisLPP === true : e.soumisLPP !== false,
  };
}

/**
 * Taux LPP de l'employé, celui par défaut à défaut.
 *
 * @param {object} [emp] employé
 * @returns {number} le taux en %, quel que soit l'interrupteur
 */
export function tauxLpp(emp) {
  return emp?.lpp ?? TAUX_LPP_DEFAUT;
}

// Chaque retenue au centime, comme la fiche officielle (PayrollCalculator) :
// le net de l'écran, calculé sur des parts non arrondies, s'écartait d'un
// centime de celui du PDF.
const part = (brut, taux) => auCentime(((+brut || 0) * (+taux || 0)) / 100);

/**
 * Retenues sociales sur un salaire brut.
 *
 * <p>L'assurance d'indemnités journalières maladie (IJM) n'a pas
 * d'interrupteur : elle se retient dès que la société règle un taux, à tout
 * le monde, comme sur la fiche officielle.
 *
 * @param {number} brut salaire brut du mois
 * @param {object} [emp] employé
 * @param {{avs?: number, ac?: number, aanp?: number, ijm?: number, canton?: number}} [taux] taux part
 *   employé de l'entreprise pour l'année de la paie ; un taux absent ou inadmissible
 *   vaut celui de {@link TAUX_FICHE}
 * @param {?Array} [lignesPays] hors de Suisse, les cotisations du pays (`lignesDuPays`) :
 *   le résultat porte alors `pays`, `detail` (part employé), `employeur` et `totalEmployeur`
 * @returns {{soumis: object, taux: {avs: number, ac: number, aanp: number, ijm: number},
 *   lpp: number, dAvs: number, dAc: number, dAanp: number, dIjm: number, dLpp: number,
 *   dCanton: number, total: number}} les retenues, zéro pour une assurance non soumise ; `taux` et `lpp`
 *   sont les taux appliqués, `lpp` zéro sans cotisation LPP
 */
export function retenuesSalariales(brut, emp, taux, lignesPays) {
  // Hors de Suisse : les cotisations du pays, ligne par ligne, et rien de
  // suisse (ni AVS, ni chômage, ni AANP, ni LPP). Leur total passe par
  // `dCanton`, que tous les écrans additionnent déjà.
  if (Array.isArray(lignesPays)) {
    const detail = lignesPays
      .filter((l) => l.sal > 0)
      .map((l) => ({ libelle: l.libelle, taux: l.sal, montant: part(brut, l.sal) }));
    const employeur = lignesPays
      .filter((l) => l.pat > 0)
      .map((l) => ({ libelle: l.libelle, taux: l.pat, montant: part(brut, l.pat) }));
    const total = auCentime(detail.reduce((a, d) => a + d.montant, 0));
    const aucun = { avs: false, ac: false, laa: false, aanp: false, lpp: false };
    return {
      pays: true,
      soumis: aucun,
      taux: { avs: 0, ac: 0, aanp: 0, ijm: 0, canton: 0 },
      lpp: 0,
      dAvs: 0,
      dAc: 0,
      dAanp: 0,
      dIjm: 0,
      dLpp: 0,
      dCanton: total,
      total,
      detail,
      employeur,
      totalEmployeur: auCentime(employeur.reduce((a, d) => a + d.montant, 0)),
    };
  }
  const saisis = taux || {};
  const t = {
    avs: tauxValide(saisis.avs, TAUX_FICHE.avs),
    ac: tauxValide(saisis.ac, TAUX_FICHE.ac),
    aanp: tauxValide(saisis.aanp, TAUX_FICHE.aanp),
    ijm: tauxValide(saisis.ijm, TAUX_FICHE.ijm),
    // Cotisations du canton (Genève, Vaud…), ou charges du pays hors de
    // Suisse : rien sans taux réglé.
    canton: tauxValide(saisis.canton, 0, TAUX_MAXIMUM_PAYS),
  };
  const soumis = assujettissements(emp);
  const lpp = soumis.lpp ? tauxLpp(emp) : 0;
  const dAvs = soumis.avs ? part(brut, t.avs) : 0;
  const dAc = soumis.ac ? part(brut, t.ac) : 0;
  const dAanp = soumis.aanp ? part(brut, t.aanp) : 0;
  const dIjm = part(brut, t.ijm);
  const dLpp = soumis.lpp ? part(brut, lpp) : 0;
  // Prélevées avec l'AVS, sur le même salaire.
  const dCanton = soumis.avs ? part(brut, t.canton) : 0;
  return {
    soumis,
    taux: t,
    lpp,
    dAvs,
    dAc,
    dAanp,
    dIjm,
    dLpp,
    dCanton,
    total: dAvs + dAc + dAanp + dIjm + dLpp + dCanton,
  };
}

/**
 * Taux écrit comme sur la fiche : deux décimales.
 *
 * @param {number} taux taux en %
 * @returns {string} par exemple « 5.30 »
 */
export function tauxEcrit(taux) {
  // Avec la virgule ou le point du pays, comme les montants.
  return (+taux || 0).toFixed(2).replace('.', decimalesDuPays());
}

/**
 * Parts patronales qui dépendent des assujettissements de l'employé.
 *
 * Les allocations familiales et les frais d'administration ne sont pas
 * concernés : l'écran les calcule sur tout le brut, comme avant.
 *
 * @param {number} brut salaire brut du mois
 * @param {object} [emp] employé
 * @param {{avsPat: number, acPat: number, aapPat: number, cantonPat?: number}} taux taux part employeur
 * @returns {{pAvs: number, pAc: number, pAap: number, pLpp: number, pCanton: number}}
 */
export function chargesPatronales(brut, emp, taux) {
  const soumis = assujettissements(emp);
  return {
    pAvs: soumis.avs ? part(brut, taux.avsPat) : 0,
    pAc: soumis.ac ? part(brut, taux.acPat) : 0,
    pAap: soumis.laa ? part(brut, taux.aapPat) : 0,
    // La part patronale LPP est estimée égale à celle de l'employé.
    pLpp: soumis.lpp ? part(brut, tauxLpp(emp)) : 0,
    // Cotisations du canton, prélevées avec l'AVS.
    pCanton: soumis.avs ? part(brut, taux.cantonPat) : 0,
  };
}
